import { useMemo, useState } from 'react';
import { DororaidIcon } from '../../../shared/ui/DororaidIcon.jsx';
import { loadPlatformUnionChatMessages } from '../api/platformChatModerationApi.js';
import { usePlatformChatModeration } from '../hooks/usePlatformChatModeration.js';
import '../../../styles/platform-chat-moderation-actions.css';

const COMMUNITY_TYPE_SHORT_LABELS = {
  gallery: '갤',
  channel: '챈',
};

const MESSAGE_TYPE_LABELS = {
  text: '일반 채팅',
  practice_report: '모의전 공유',
  attack_order: '공격 지시',
  raid_result: '실전 딜 반영',
  system: '시스템',
};

function formatDateTime(value) {
  if (!value) return '시간 확인 불가';
  try {
    return new Intl.DateTimeFormat('ko-KR', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(new Date(value));
  } catch {
    return '시간 확인 불가';
  }
}

function formatCsvDateTime(value) {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('ko-KR', {
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(new Date(value));
  } catch {
    return String(value || '');
  }
}

function csvCell(value) {
  let text = String(value ?? '').replace(/\r\n?/g, '\n');
  if (/^[=+\-@\t]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
}

function safeFileSegment(value, fallback) {
  const normalized = String(value || '')
    .normalize('NFKC')
    .replace(/[\\/:*?"<>|\s]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
  return normalized || fallback;
}

function csvFileStamp() {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return formatter.format(now).replace(/[^0-9]/g, '').slice(0, 12);
}

function normalizedNikkeRef(raw = {}) {
  return {
    id: String(raw.nikke_id ?? raw.nikkeId ?? raw.code ?? raw.id ?? raw.name ?? ''),
    name: String(raw.name ?? raw.official_name ?? raw.officialName ?? '니케'),
    imageUrl: String(raw.image_url ?? raw.imageUrl ?? ''),
  };
}

function MessageNikkeRefs({ refs }) {
  const items = (Array.isArray(refs) ? refs : []).map(normalizedNikkeRef).filter((item) => item.id || item.name);
  if (!items.length) return null;
  return <div className="platform-chat-message-nikkes">{items.map((item, index) => <span key={`${item.id}:${index}`}>{item.imageUrl ? <img src={siteImageUrl(item.imageUrl)} alt="" /> : null}<b>{item.name}</b></span>)}</div>;
}

export function PlatformChatModerationBoard({ mobile = false }) {
  const state = usePlatformChatModeration();
  const [typeFilter, setTypeFilter] = useState('all');
  const [visibilityFilter, setVisibilityFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [reasons, setReasons] = useState({});
  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportNotice, setExportNotice] = useState({ type: '', message: '' });

  const filteredMessages = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('ko-KR');
    return state.timeline.messages.filter((message) => {
      if (typeFilter !== 'all' && message.messageType !== typeFilter) return false;
      if (visibilityFilter === 'visible' && message.deletedAt) return false;
      if (visibilityFilter === 'hidden' && !message.deletedAt) return false;
      if (!needle) return true;
      return `${message.authorNickname} ${message.body} ${message.moderation?.reason || ''}`
        .toLocaleLowerCase('ko-KR')
        .includes(needle);
    });
  }, [query, state.timeline.messages, typeFilter, visibilityFilter]);

  const selectedSeason = state.selectedSeason;
  const hiddenCount = state.timeline.messages.filter((message) => message.deletedAt).length;
  const visibleNotice = exportNotice.message ? exportNotice : state.notice;

  const handleModeration = async (message) => {
    const reason = String(reasons[message.id] || '').trim();
    const action = message.deletedAt ? 'restore' : 'hide';
    const prompt = action === 'restore'
      ? '이 메시지를 유니온 채팅에 다시 표시할까요?'
      : '이 메시지를 유니온 채팅에서 숨길까요?';
    if (!window.confirm(prompt)) return;
    const success = await state.moderate({ messageId: message.id, action, reason });
    if (success) setReasons((current) => ({ ...current, [message.id]: '' }));
  };

  const handlePurge = async (message) => {
    const reason = String(reasons[message.id] || '').trim();
    if (!window.confirm('이 메시지를 영구 삭제할까요? 감사 로그만 남고 되돌릴 수 없습니다.')) return;
    const success = await state.moderate({ messageId: message.id, action: 'purge', reason });
    if (success) setReasons((current) => ({ ...current, [message.id]: '' }));
  };

  const handlePurgeSeason = async () => {
    if (!state.selectedUnionId || !state.selectedSeasonId || state.purgingSeason) return;
    const seasonLabel = selectedSeason?.seasonNo ? `${selectedSeason.seasonNo}차` : '이 차수';
    const messageCount = selectedSeason?.messageCount ?? state.timeline.messages.length;
    const confirmed = window.confirm(
      `${state.timeline.union?.unionName || state.selectedUnion?.unionName || '유니온'} ${seasonLabel} 채팅 전체(약 ${messageCount}건)를 영구 삭제할까요?\n\n감사 로그만 남고 메시지는 되돌릴 수 없습니다.`,
    );
    if (!confirmed) return;
    const reason = window.prompt('삭제 사유(선택, 감사 로그에 남습니다)', '') || '';
    await state.purgeSeason(reason);
  };

  const handleCsvExport = async () => {
    if (!state.selectedUnionId || !state.selectedSeasonId || exportingCsv) return;
    setExportingCsv(true);
    setExportNotice({ type: '', message: '' });
    try {
      const exportTimeline = await loadPlatformUnionChatMessages({
        unionId: state.selectedUnionId,
        seasonId: state.selectedSeasonId,
        union: state.timeline.union || state.selectedUnion,
        season: state.timeline.season || selectedSeason,
        includeHidden: true,
        limit: 500,
      });
      const textMessages = exportTimeline.messages
        .filter((message) => message.messageType === 'text')
        .sort((left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime());
      if (!textMessages.length) {
        setExportNotice({ type: 'error', message: '저장할 일반 채팅 메시지가 없습니다.' });
        return;
      }

      const union = exportTimeline.union || state.timeline.union || state.selectedUnion;
      const season = exportTimeline.season || selectedSeason;
      const headers = [
        '유니온',
        '길드 ID',
        '레이드 차수',
        '작성 시각',
        '작성자',
        '메시지',
        '표시 상태',
        '숨김 시각',
        '최근 처리',
        '처리 사유',
        '처리 관리자',
        '처리 시각',
        '메시지 ID',
        '작성자 ID',
        '답글 대상 ID',
      ];
      const rows = textMessages.map((message) => [
        union?.unionName || '유니온',
        union?.guildId || '',
        season?.seasonNo ? `${season.seasonNo}차` : '',
        formatCsvDateTime(message.createdAt),
        message.authorNickname,
        message.body,
        message.deletedAt ? '숨김' : '표시 중',
        formatCsvDateTime(message.deletedAt),
        message.moderation?.action === 'hide' ? '숨김' : message.moderation?.action === 'restore' ? '복원' : '',
        message.moderation?.reason || '',
        message.moderation?.actorLabel || '',
        formatCsvDateTime(message.moderation?.createdAt),
        message.id,
        message.authorUserId,
        message.replyToId,
      ]);
      const csv = `\uFEFF${[headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n')}`;
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const unionName = safeFileSegment(union?.unionName, 'union');
      const seasonNo = Number(season?.seasonNo || 0);
      link.href = url;
      link.download = `${unionName}_${seasonNo ? `${seasonNo}차_` : ''}일반채팅_${csvFileStamp()}.csv`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      setExportNotice({ type: 'success', message: `일반 채팅 ${textMessages.length}건을 CSV로 저장했습니다.` });
    } catch (error) {
      setExportNotice({
        type: 'error',
        message: error instanceof Error ? error.message : '일반 채팅 CSV 저장에 실패했습니다.',
      });
    } finally {
      setExportingCsv(false);
    }
  };

  return <section className={`platform-chat-moderation ${mobile ? 'mobile' : 'desktop'}`} aria-label="유니온 채팅 검수 관리">
    <header className="platform-chat-moderation-heading">
      <div>
        <h2>유니온 채팅 관리</h2>
        <p>플랫폼 관리자가 유니온별 채팅을 검수하고 부적절한 메시지를 숨김·복원합니다.</p>
      </div>
      <span>검수 전용 · 메시지 작성 및 공격 지시 불가</span>
    </header>

    <div className="platform-chat-moderation-controls">
      <label>
        <span>유니온</span>
        <select value={state.selectedUnionId} disabled={state.loadingUnions} onChange={(event) => state.selectUnion(event.target.value)}>
          {state.unions.length ? state.unions.map((union) => <option key={union.unionId} value={union.unionId}>[{COMMUNITY_TYPE_SHORT_LABELS[union.communityType] || '?'}] {union.unionName}{union.guildId ? ` · ${union.guildId}` : ''}</option>) : <option value="">활성 유니온 없음</option>}
        </select>
      </label>
      <label>
        <span>레이드 차수</span>
        <select value={state.selectedSeasonId} disabled={!state.selectedUnion?.seasons?.length} onChange={(event) => state.selectSeason(event.target.value)}>
          {state.selectedUnion?.seasons?.length ? state.selectedUnion.seasons.map((season) => <option key={season.seasonId} value={season.seasonId}>{season.seasonNo}차 · {season.status === 'active' ? '진행 중' : season.status} · 메시지 {season.messageCount}건{season.hiddenCount ? ` · 숨김 ${season.hiddenCount}` : ''}</option>) : <option value="">차수 없음</option>}
        </select>
      </label>
      <label>
        <span>메시지 유형</span>
        <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
          <option value="all">전체 유형</option>
          {Object.entries(MESSAGE_TYPE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </label>
      <label>
        <span>표시 상태</span>
        <select value={visibilityFilter} onChange={(event) => setVisibilityFilter(event.target.value)}>
          <option value="all">전체 상태</option>
          <option value="visible">표시 중</option>
          <option value="hidden">숨김</option>
        </select>
      </label>
      <label className="platform-chat-moderation-search">
        <span>검색</span>
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="닉네임·메시지·처리 사유" />
      </label>
      <button type="button" className="platform-chat-moderation-refresh" disabled={!state.selectedUnionId || state.loadingMessages} onClick={() => state.refreshMessages()} aria-label="채팅 검수 목록 새로고침" title="새로고침"><DororaidIcon name="refresh" size={19} /></button>
    </div>

    <div className="platform-chat-moderation-summary">
      <div className="platform-chat-moderation-summary-copy"><strong>{state.timeline.union?.unionName || state.selectedUnion?.unionName || '유니온 선택 필요'}</strong><span>{selectedSeason ? `${selectedSeason.seasonNo}차` : '차수 없음'}</span></div>
      <div className="platform-chat-moderation-summary-actions">
        <dl><div><dt>조회</dt><dd>{state.timeline.messages.length}</dd></div><div><dt>숨김</dt><dd>{hiddenCount}</dd></div><div><dt>필터 결과</dt><dd>{filteredMessages.length}</dd></div></dl>
        <button type="button" className="platform-chat-csv-export" disabled={!state.selectedUnionId || !state.selectedSeasonId || state.loadingMessages || exportingCsv} onClick={handleCsvExport}>{exportingCsv ? 'CSV 저장 중…' : '일반 채팅 CSV'}</button>
        <button type="button" className="platform-chat-season-purge" disabled={!state.selectedUnionId || !state.selectedSeasonId || state.loadingMessages || state.purgingSeason} onClick={handlePurgeSeason}>{state.purgingSeason ? '삭제 중…' : '차수 채팅 전체 삭제'}</button>
      </div>
    </div>

    {state.loadingUnions || state.loadingMessages ? <div className="platform-chat-moderation-loading" role="status"><span /><p>유니온 채팅을 불러오고 있습니다.</p></div> : !state.selectedUnionId ? <div className="platform-chat-moderation-empty"><strong>검수할 활성 유니온이 없습니다.</strong><p>활성화된 유니온과 레이드 차수가 생성되면 이곳에 표시됩니다.</p></div> : filteredMessages.length === 0 ? <div className="platform-chat-moderation-empty"><strong>조건에 맞는 메시지가 없습니다.</strong><p>유형·상태·검색 조건을 변경해 확인해주세요.</p></div> : <div className="platform-chat-moderation-list">
      {filteredMessages.map((message) => {
        const hidden = Boolean(message.deletedAt);
        const reason = reasons[message.id] || '';
        const working = state.workingMessageId === message.id;
        return <article className={`platform-chat-moderation-card ${hidden ? 'hidden' : ''}`} key={message.id}>
          <header>
            <div><span>{MESSAGE_TYPE_LABELS[message.messageType] || message.messageType}</span><strong>{message.authorNickname}</strong></div>
            <div><time>{formatDateTime(message.createdAt)}</time>{hidden ? <b>숨김</b> : <b className="visible">표시 중</b>}</div>
          </header>
          <p className="platform-chat-moderation-body">{message.body}</p>
          <MessageNikkeRefs refs={message.nikkeRefs} />
          {message.moderation ? <p className="platform-chat-moderation-audit"><strong>{message.moderation.action === 'hide' ? '최근 숨김' : '최근 복원'}</strong><span>{message.moderation.actorLabel} · {formatDateTime(message.moderation.createdAt)}</span><em>{message.moderation.reason || '사유 없음'}</em></p> : null}
          <div className="platform-chat-moderation-action">
            <label><span>{hidden ? '복원 사유' : '숨김 사유'} · 선택</span><input maxLength={500} value={reason} onChange={(event) => setReasons((current) => ({ ...current, [message.id]: event.target.value }))} placeholder="입력하지 않아도 처리할 수 있습니다." /></label>
            <button type="button" className={hidden ? 'restore' : 'hide'} disabled={working} onClick={() => handleModeration(message)}>{working ? '처리 중…' : hidden ? '메시지 복원' : '메시지 숨김'}</button>
            <button type="button" className="purge" disabled={working} onClick={() => handlePurge(message)}>{working ? '처리 중…' : '완전 삭제'}</button>
          </div>
        </article>;
      })}
    </div>}

    {visibleNotice.message ? <p className={`platform-chat-moderation-notice ${visibleNotice.type}`} role="status">{visibleNotice.message}</p> : null}
  </section>;
}

import { siteImageUrl } from '../../../shared/domain/siteImages.js';